import React, { useMemo } from 'react';
import {
  AccordionItem,
  AppButton,
  Box,
  CrossIcon,
  Inscription,
  OrganizationHierarchyIcon,
  Preloader,
  RadioButton,
  SearchInput,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { OrganizationTypes } from '@/common/constants';
import { Organization } from '@/modules/core/queries/useGetOrganization.query';
import useGetOrganizationHierarchyQuery from '@/modules/core/queries/useGetOrganizationHierarchy.query';
import useGetOrgName from '@/modules/employer/queries/useGetOrgName.query';

import { SelectedOrganization } from '../../types';
import useSingleOrganizationSelector from '../hooks/useSingleOrganizationSelector';

import { SingleNestedSelectorLevel, StyledItem } from './SingleNestedSelectorLevel';

const StyledButton = styled(Box)`
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  &:hover {
    background-color: ${({ theme }) => theme.colors.dangerHover};
  }
`;

interface Props {
  testId: string;
  value: SelectedOrganization | null;
  onChange: (value: SelectedOrganization | null) => void;
}

const findOrganizationTree = (parentId: number | string, data: Organization[]): Organization[] | undefined => {
  const findOrganization = (node: Organization): Organization | undefined => {
    if (+node.id === +parentId) {
      return node;
    } else if (node.children) {
      for (const child of node.children) {
        const result = findOrganization(child);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  };

  for (const org of data) {
    const parentOrganization = findOrganization(org);
    if (parentOrganization) {
      return [parentOrganization];
    }
  }
  return undefined;
};

export const SingleOrganizationSelector: React.FC<Props> = ({
  testId,
  value,
  onChange,
}) => {
  const {
    ref,
    inputRef,
    isShown,
    setIsShown,
    searchString,
    setSearchString,
    searchValue,
    isSearchUsed,
    partnerParentId,
    distributorParentId,
    selectedTemp,
    setSelectedTemp,
    id,
    type,
    name,
    isSystemLevel,
    isPartnerLevel,
    isDistributorLevel,
    isCompanyLevel,
    currentOrgLevels,
    systemId,
    handleApply,
    handleCancel,
  } = useSingleOrganizationSelector({ value, onChange });

  const {
    searchedPartners,
    isLoading,
  } = useGetOrganizationHierarchyQuery({
    ...(isSearchUsed ? { searchValue } : {}),
    isCompanyLevel,
    partnerParentId: isPartnerLevel ? id : partnerParentId,
    distributorParentId: isDistributorLevel ? id : distributorParentId,
  });

  const currentSearchedOrgs = useMemo(() => {
    if (isPartnerLevel) return searchedPartners;
    return findOrganizationTree(id, searchedPartners || []);
  }, [id, isPartnerLevel, searchedPartners]);

  const systemNameData = useGetOrgName({
    id: systemId,
    enabled: isSystemLevel,
  });

  return (
    <Box ref={ref} style={{ position: 'relative' }}>
      <Box direction="row" align="center">
        <Box width="220px" style={{ overflow: 'hidden' }}>
          <AppButton
            buttonType="secondary"
            testId={`${testId}_SingleOrgSelect_dropdown`}
            onClick={() => setIsShown((prev) => !prev)}
            rightIcon={<OrganizationHierarchyIcon />}
            width='220px'
          >
            <Inscription
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value ? value.name : 'Search Organizations'}
            </Inscription>
          </AppButton>
        </Box>

        {value ? (
          <StyledButton
            margin={{ left: 'spacing8' }}
            width="20px"
            height="20px"
            round="button2Round"
            background="danger"
            align="center"
            justify="center"
            onClick={() => onChange(null)}
            data-testid={`${testId}_SingleOrgSelect_clear-button`}
          >
            <CrossIcon size='10px' color="iconOnColor" />
          </StyledButton>
        ) : null}
      </Box>

      {isShown && (
        <Box
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99,
            pointerEvents: 'none',
          }}
          background="popup2"
        />
      )}

      <Box
        background="canvas"
        round="fieldRound"
        width={{ min: '340px' }}
        elevation="active"
        style={{
          position: 'absolute',
          right: 'calc(100% + 4px)',
          top: '50%',
          transform: 'translateY(-30%)',
          zIndex: 100,
          ...(isShown ? {} : { display: 'none' }),
        }}
      >
        <Box
          pad={{ horizontal: 'spacing16', top: 'spacing12', bottom: 'spacing12' }}
          border={{ side: 'bottom', color: 'border1' }}
        >
          <SearchInput
            inputRef={inputRef}
            placeholder="Search"
            testId={`${testId}_SingleOrgSelect_search`}
            value={searchString}
            onChange={setSearchString}
          />
        </Box>

        <Box style={{ maxHeight: '340px', overflowY: 'auto' }}>
          {isLoading ? (
            <Box justify="center" align="center" pad="spacing12">
              <Preloader testId="preloader" size={24} />
            </Box>
          ) : (
            <Box pad='spacing16' style={{ overflowX: 'hidden' }}>
              {isSystemLevel ? (
                <AccordionItem
                  testId="system-level"
                  isOptionViewArrow
                  text={
                    <StyledItem
                      data-testid="system-level-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTemp({
                          id: systemId,
                          name: (systemNameData?.name || name) as string,
                          type: OrganizationTypes.system,
                        });
                      }}
                      pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
                      margin={{ left: 'spacing2' }}
                      round="dropdownItemRound"
                      direction="row"
                      width="100%"
                    >
                      <RadioButton
                        id={`radio-node-${systemId}`}
                        name="single-org-selector-radio"
                        checked={selectedTemp?.id === systemId}
                        onChange={() =>
                          setSelectedTemp({
                            id: systemId,
                            name: (systemNameData?.name || name) as string,
                            type: OrganizationTypes.system,
                          })}
                        label={
                          <Text color="textBody" weight="normal">
                            {isSystemLevel ? name : systemNameData?.name} (System)
                          </Text>
                        }
                      />
                    </StyledItem>
                  }
                  isExpandedDefault
                  isLeftArrow
                >
                  <SingleNestedSelectorLevel
                    orgLevels={currentOrgLevels}
                    parentId={systemId}
                    selectedOrgId={selectedTemp?.id || ''}
                    onSelect={setSelectedTemp}
                    isSearchUsed={isSearchUsed}
                    searchValue={searchValue}
                    isShown={isShown}
                    isLoading={isLoading}
                    currentHightLevel={type}
                    searchedOrgs={searchedPartners}
                  />
                </AccordionItem>
              ) : (
                <SingleNestedSelectorLevel
                  orgLevels={currentOrgLevels}
                  selectedOrgId={selectedTemp?.id || ''}
                  onSelect={setSelectedTemp}
                  isSearchUsed={isSearchUsed}
                  searchValue={searchValue}
                  isShown={isShown}
                  isLoading={isLoading}
                  currentHightLevel={type}
                  searchedOrgs={currentSearchedOrgs}
                />
              )}
            </Box>
          )}
        </Box>

        <Box
          direction="row"
          justify="between"
          align="center"
          pad='spacing16'
          border={{ side: 'top', color: 'border1' }}
        >
          <AppButton
            buttonType="secondary"
            width="145px"
            onClick={handleCancel}
            testId={`${testId}_SingleOrgSelect_cancel`}
          >
            Cancel
          </AppButton>
          <AppButton
            width="145px"
            onClick={handleApply}
            testId={`${testId}_SingleOrgSelect_apply`}
          >
            Apply
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};

export default SingleOrganizationSelector;
